/**
 * #39 · Detalle de un lote.
 *
 * Si el lote está retirado, lo primero de la pantalla es la banda de retiro
 * con su fecha, y el botón de retirar no existe. Un lote retirado no se puede
 * volver a retirar (la API responde `batch_already_recalled`) y, sobre todo,
 * no se puede des-retirar: no hay endpoint y no debería haberlo.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';

import { useApiQuery } from '@/api/hooks';
import { batchKeys } from '@/api/keys';
import type { Schemas } from '@/api/types';
import { useApi } from '@/api/useApi';
import { useAuth } from '@/auth/useAuth';
import { useI18n } from '@/i18n/useI18n';
import { Badge } from '@/ui/Badge';
import { Button } from '@/ui/Button';
import { Card, CardBody, CardHeader } from '@/ui/Card';
import { ErrorState, QueryBoundary } from '@/ui/states';
import { riskTone } from '../products/catalog';
import { Dialog } from '../products/ui/Dialog';
import { DetailList, DetailRow } from '../products/ui/fields';
import { BatchForm, type BatchDraft } from './BatchForm';
import { RecallDialog } from './RecallDialog';
import { BATCH_STATUS_LABEL_KEYS, BATCH_STATUS_TONES, isBatchStatus } from './status';

type Batch = Schemas['BatchOut'];

export function BatchDetailPage(): ReactNode {
  const { t, fmt } = useI18n();
  const { can } = useAuth();
  const api = useApi();
  const queryClient = useQueryClient();
  const params = useParams<{ batchId: string }>();
  const batchId = Number(params.batchId);
  const valid = Number.isInteger(batchId) && batchId > 0;

  const [editing, setEditing] = useState(false);
  const [recalling, setRecalling] = useState(false);

  const batch = useApiQuery<Batch>(
    batchKeys.detail(batchId),
    `/api/v1/batches/${String(batchId)}`,
    undefined,
    { enabled: valid },
  );

  const update = useMutation<Batch, unknown, BatchDraft>({
    mutationFn: (draft) =>
      api.patch<Batch>(`/api/v1/batches/${String(batchId)}`, {
        lot_code: draft.lot_code,
        expiry_date: draft.expiry_date,
        territory: draft.territory,
        status: draft.status,
      }),
    onSuccess: () => {
      setEditing(false);
      void queryClient.invalidateQueries({ queryKey: batchKeys.all });
    },
  });

  if (!valid) {
    return (
      <div className="mx-auto max-w-3xl">
        <Card>
          <CardBody>
            <ErrorState error={new Error(t('batches.detail.badId'))} />
          </CardBody>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <Link to="/batches" className="inline-block text-sm text-accent underline underline-offset-2">
        {t('batches.back')}
      </Link>

      <QueryBoundary query={batch}>
        {(data) => {
          const recalled = data.status === 'recalled';
          return (
            <>
              {recalled && (
                <div
                  role="alert"
                  className="rounded-(--radius-card) border border-critical/30 bg-critical-soft p-5"
                >
                  <p className="text-sm font-semibold text-critical">
                    {t('batches.recalled.banner')}
                  </p>
                  <p className="mt-1 text-sm text-ink">
                    {t('batches.recalled.detail', {
                      lot: data.lot_code,
                      when: fmt.dateTime(data.recalled_at),
                    })}
                  </p>
                </div>
              )}

              <Card>
                <CardHeader
                  title={data.lot_code}
                  description={data.product_sku ?? `#${String(data.product_id)}`}
                  actions={
                    <>
                      {can('analyst') && !recalled && (
                        <Button
                          size="sm"
                          onClick={() => {
                            update.reset();
                            setEditing(true);
                          }}
                        >
                          {t('batches.edit')}
                        </Button>
                      )}
                      {can('admin') && !recalled && (
                        <Button
                          size="sm"
                          variant="critical"
                          onClick={() => {
                            setRecalling(true);
                          }}
                        >
                          {t('batches.recall')}
                        </Button>
                      )}
                    </>
                  }
                />
                <CardBody>
                  <DetailList>
                    <DetailRow
                      label={t('batches.column.product')}
                      value={
                        <Link
                          to={`/products/${String(data.product_id)}`}
                          className="text-accent underline underline-offset-2"
                        >
                          {data.product_sku ?? `#${String(data.product_id)}`}
                        </Link>
                      }
                    />
                    <DetailRow
                      label={t('batches.column.lot')}
                      value={<code className="font-mono">{data.lot_code}</code>}
                    />
                    <DetailRow label={t('batches.column.expiry')} value={fmt.expiry(data.expiry_date)} />
                    <DetailRow
                      label={t('batches.column.territory')}
                      value={data.territory ?? t('batches.territory.none')}
                    />
                    <DetailRow
                      label={t('batches.column.status')}
                      value={
                        <Badge
                          tone={isBatchStatus(data.status) ? BATCH_STATUS_TONES[data.status] : 'neutral'}
                        >
                          {isBatchStatus(data.status)
                            ? t(BATCH_STATUS_LABEL_KEYS[data.status])
                            : data.status}
                        </Badge>
                      }
                    />
                    <DetailRow
                      label={t('batches.column.risk')}
                      value={<Badge tone={riskTone(data.risk_score)}>{fmt.number(data.risk_score)}</Badge>}
                    />
                    <DetailRow
                      label={t('batches.column.activated')}
                      value={
                        <span className="text-base font-semibold tabular-nums">
                          {fmt.number(data.activated_count)}
                        </span>
                      }
                    />
                    <DetailRow
                      label={t('batches.recalledAt')}
                      value={recalled ? fmt.dateTime(data.recalled_at) : '—'}
                    />
                    <DetailRow label={t('batches.column.created')} value={fmt.dateTime(data.created_at)} />
                    <DetailRow label={t('batches.column.updated')} value={fmt.dateTime(data.updated_at)} />
                  </DetailList>
                  <p className="mt-4 text-xs text-ink-subtle">{t('batches.activated.hint')}</p>
                </CardBody>
              </Card>

              <Dialog
                open={editing}
                onClose={() => {
                  setEditing(false);
                }}
                title={t('batches.edit.title')}
                description={data.lot_code}
              >
                <BatchForm
                  batch={data}
                  products={[]}
                  onSubmit={(draft) => {
                    update.mutate(draft);
                  }}
                  onCancel={() => {
                    setEditing(false);
                  }}
                  isSubmitting={update.isPending}
                  error={update.error}
                />
              </Dialog>

              {/* Montado sólo mientras el retiro está en curso: ver la
                  cabecera de `RecallDialog`. */}
              {recalling && (
                <RecallDialog
                  batch={data}
                  onClose={() => {
                    setRecalling(false);
                  }}
                />
              )}
            </>
          );
        }}
      </QueryBoundary>
    </div>
  );
}
